import { useCallback, useEffect, useRef, useState } from 'react'

/** How long a confirmation stays up. One duration, so they all read alike. */
const FLASH_MS = 1200

/**
 * A value that says something happened and then stops saying it — the "copied"
 * on a chip somebody just clicked.
 *
 * The value is the answer rather than a boolean, so a list flashing WHICH of
 * its rows was taken uses this too instead of tracking the name beside a flag.
 *
 * It clears on unmount and on the next flash: a surface closed mid-flash leaves
 * no timer running, and clicking a second chip does not let the first one's
 * timer put the message away early.
 */
export function useFlash<T>(ms: number = FLASH_MS): [T | null, (v: T) => void] {
  const [shown, setShown] = useState<T | null>(null)
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => () => window.clearTimeout(timer.current), [])

  const flash = useCallback(
    (value: T) => {
      setShown(value)
      window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => setShown(null), ms)
    },
    [ms],
  )

  return [shown, flash]
}
